/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable camelcase */
import type { DraftAsset } from '~publish/components/Composer/assets/assetListOps'
import { getSavedVideoAudioCodec } from '~publish/helpers/videoAudioMetadata'
import type { Service } from '~publish/components/Composer/graphqlEnums'
import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import {
  AttachmentTypes,
  MediaTypes,
  Services,
} from '~publish/legacy/constants'
import type {
  FacebookMentionDbEntry,
  FacebookMentionEntity,
} from '~publish/legacy/editor/plugins/facebook-mention/nodes'
import {
  isRpcUpdateDocument,
  isRpcUpdateGif,
  isRpcUpdateImage,
  isRpcUpdateImageOrGif,
  isRpcUpdateLinkMedia,
  isRpcUpdateVideo,
} from '~publish/legacy/post/helpers'
import type {
  RpcUpdate,
  RpcUpdateCommonContent,
  RpcUpdateImage,
  RpcUpdateLinkMedia,
  RpcUpdateMedia,
  RpcUpdateRetweetData,
  RpcUpdateVideo,
} from '~publish/legacy/post/types'
import type { Profile } from '~publish/legacy/profile-sidebar/types'

import { createPreviewProfile } from './ProfileUtils'
import { Document } from '../entities/Document'
import type { Thread } from '../entities/Draft/Draft'
import { getNewGif, getNewImage } from '../entities/factories'
import type { Link, Retweet, Video } from '../entities/factories'
import type { AppStoreMetaData, ComposerProfile } from '../stores/types'

const getFormattedFacebookMentionEntities = (
  entities: FacebookMentionDbEntry[],
): FacebookMentionEntity[] =>
  entities.map(({ indices, content, text, url }) => ({
    indices: [+indices[0], +indices[1]],
    id: `${+content}`,
    name: text,
    url,
  }))

const getFormattedRetweetData = ({
  text,
  comment,
  tweet_id,
  user_id,
  username,
  profile_name,
  url,
  avatars,
  media = [],
}: RpcUpdateRetweetData): Retweet => ({
  text,
  comment,
  tweetId: `${tweet_id}`,
  userId: `${user_id}`,
  userName: username,
  userDisplayName: profile_name,
  tweetUrl: url,
  avatarUrl: avatars?.https ?? '',
  media: media || [],
})

const getFormattedLinkData = (linkData: RpcUpdateLinkMedia): Link => {
  const { link, title, description, preview, expanded_link, original_preview } =
    linkData
  const thumbnail = getNewImage({
    url: preview || '',
    originalUrl: original_preview,
  })
  return {
    url: link,
    title,
    description,
    thumbnail,
    expandedUrl: expanded_link || null,
    thumbnailHttps: preview || null,
    availableThumbnails: [thumbnail],
    wasEdited: false,
  }
}

const formatImage = (image: RpcUpdateImage): RpcUpdateImage => ({
  ...image,
  picture: image.picture ?? image.photo,
  alt_text: image.alt_text === 'null' ? null : image.alt_text,
})

const formatRootImages = (
  media?: RpcUpdateMedia,
  extra_media?: RpcUpdateMedia[],
): RpcUpdateImage[] | null => {
  const filteredImages = [media, ...(extra_media || [])]
    .filter(isRpcUpdateImageOrGif)
    .map(formatImage)
  return filteredImages.length > 0 ? filteredImages : null
}

// Every video across media + extra_media, not just the first — a post
// carrying more than one (e.g. an Instagram feed post) must hydrate all of
// them on load, the same way formatRootImages does for images.
const formatRootVideos = (
  media?: RpcUpdateMedia,
  extra_media?: RpcUpdateMedia[],
): Video[] => {
  return [media, ...(extra_media || [])]
    .filter(isRpcUpdateVideo)
    .map(getFormattedVideoData)
}

// True cross-kind attach order, read off the same [media, ...extra_media]
// list before formatRootImages/video/document split it into type-bucketed
// AppStoreMetaData fields. AppInitActionCreators.loadInitialMetaData uses
// this to restore true interleaving once every bucket has loaded.
const tagOfMedia = (
  item: RpcUpdateMedia | undefined,
): DraftAsset['kind'] | null => {
  if (isRpcUpdateImage(item)) return 'image'
  if (isRpcUpdateGif(item)) return 'gif'
  if (isRpcUpdateVideo(item)) return 'video'
  if (isRpcUpdateDocument(item)) return 'document'
  return null
}

const formatMediaOrder = (
  media?: RpcUpdateMedia,
  extra_media?: RpcUpdateMedia[],
): DraftAsset['kind'][] | null => {
  const order = [media, ...(extra_media ?? [])]
    .map(tagOfMedia)
    .filter((tag): tag is DraftAsset['kind'] => tag !== null)
  return order.length > 0 ? order : null
}

/**
 * Extracts the upload ID from a Buffer video URL.
 * URL format: https://buffer-media-uploads.s3.amazonaws.com/{userId}/{uploadId}/{filename}
 *
 * Note: This function is a workaround. Some code paths remove video IDs from the database,
 * causing issues when duplicating posts (e.g., Instagram video posts). This extracts the
 * upload ID from the URL as a fallback when the ID is missing.
 *
 * @param url - The video URL
 * @returns The upload ID (24-character MongoDB ObjectId) or null if not found/invalid
 */
export const extractUploadIdFromVideoUrl = (
  url: string | undefined,
): string | null => {
  if (!url) return null

  try {
    // Match the pattern: https://buffer-media-uploads.s3.amazonaws.com/userId/uploadId/filename
    const match = url.match(
      /^https:\/\/buffer-media-uploads\.s3\.amazonaws\.com\/[^/]+\/([^/]+)\//,
    )
    const uploadId = match ? match[1] : null

    // Validate upload ID is a valid MongoDB ObjectId (24-character hex string)
    if (uploadId && !/^[a-f0-9]{24}$/i.test(uploadId)) {
      return null
    }

    return uploadId
  } catch {
    return null
  }
}

const getFormattedVideoData = (videoData: RpcUpdateVideo): Video => {
  // Use the video.id if available, otherwise extract from the location URL
  const uploadId =
    videoData.video.id ||
    extractUploadIdFromVideoUrl(videoData.video.details?.location)

  return {
    uploadId,
    name: videoData.video.title,
    availableThumbnails: videoData.video.thumbnails,
    duration: videoData.video.details?.duration,
    durationMs: videoData.video.details?.duration_millis,
    audioCodec: getSavedVideoAudioCodec(videoData.video.details),
    size: videoData.video.details?.file_size,
    width: videoData.video.details?.width,
    height: videoData.video.details?.height,
    // Duplicating an older post can carry a video whose transcode never
    // populated `transcoded_location` (mirrors the fallback in
    // entities/src/update/value-objects/Asset.ts#getSource); without it,
    // `url` goes out empty and GraphQL rejects the required VideoAssetInput.url.
    url:
      videoData.video.details?.transcoded_location ||
      videoData.video.details?.location,
    originalUrl: videoData.video.details?.location,
    thumbOffset: videoData.video.thumb_offset,
    thumbnail: videoData.thumbnail,
    mediaType: MediaTypes.VIDEO,
    wasEdited: false,
  }
}

const formatThreadsData = (
  threads: RpcUpdateCommonContent[] | null | undefined,
): Thread | null => {
  return (
    threads?.map((thread) => {
      const retweet = thread.retweet
        ? getFormattedRetweetData(thread.retweet)
        : null
      const text =
        ((!thread.retweet || !thread.retweet.comment) && thread.text) || ''
      const images = isRpcUpdateImage(thread?.media)
        ? [thread.media, ...(thread.extra_media ?? [])]
            .filter(isRpcUpdateImage)
            .map((image) =>
              getNewImage({
                url: image.picture ?? image.photo,
                altText: image.alt_text === 'null' ? null : image.alt_text,
                source: image.source,
                height: image.height ? Number(image.height) : null,
                width: image.width ? Number(image.width) : null,
              }),
            )
        : []

      const gifMedia = isRpcUpdateGif(thread.media) ? thread.media : null
      const gifUrl = gifMedia?.picture ?? gifMedia?.photo
      const gif =
        gifMedia && gifUrl
          ? getNewGif(
              gifUrl,
              gifUrl,
              gifMedia.width ? Number(gifMedia.width) : null,
              gifMedia.height ? Number(gifMedia.height) : null,
            )
          : null
      const videos = formatRootVideos(thread.media, thread.extra_media)
      const video = videos[0] ?? null
      const link = isRpcUpdateLinkMedia(thread.media)
        ? getFormattedLinkData(thread.media)
        : null

      const enabledAttachmentTypeLink = link ? AttachmentTypes.LINK : null
      const enabledAttachmentTypeRetweet = retweet
        ? AttachmentTypes.RETWEET
        : null
      const enabledAttachmentType =
        enabledAttachmentTypeLink ||
        enabledAttachmentTypeRetweet ||
        AttachmentTypes.MEDIA
      return {
        text,
        characterCount: text.length,
        images,
        gif,
        video,
        videos,
        link,
        retweet,
        enabledAttachmentType,
        // I strongly suspect threads never have 'urls' or 'unshortenedUrls' set
        // but the composer deliberates creates overlap in post and thread types
        // in an attempt to be able to use them interchangeably and I'm not going
        // to mess with that right now.
        urls: [],
        unshortenedUrls: [],
      }
    }) ?? null
  )
}

const previewProfileExcludedServices: ReadonlySet<Partial<Service> | 'omni'> =
  new Set(['googlebusiness', 'startPage', 'pinterest', 'omni'])

const DataImportUtils = {
  formatProfilesData: ({
    profiles,
    previewMode = !profiles?.length,
    preselectedProfileIds,
    subprofileId,
    userAvatar,
  }: {
    profiles: Profile[]
    previewMode?: boolean
    preselectedProfileIds: string[]
    subprofileId?: string
    userAvatar?: string
  }): Array<ComposerProfile> => {
    if (!profiles?.length && previewMode) {
      return Object.values(Services)
        .filter((service) => !previewProfileExcludedServices.has(service.name))
        .map((service) => createPreviewProfile(service.name, userAvatar))
    }

    return profiles.map((importedProfile) => {
      const subprofiles = importedProfile.subprofiles
        ? importedProfile.subprofiles.map(
            ({ name, avatar, id, service_id, profile_id, is_shared }) => ({
              id,
              serviceId: service_id,
              name,
              avatar,
              profileId: profile_id,
              isShared: is_shared,
              shouldBeAutoSelected: id === subprofileId,
            }),
          )
        : []

      return {
        id: importedProfile.id,
        apiVersion: importedProfile.api_version,
        serviceName: importedProfile.service,
        serviceUsername: importedProfile.service_username,
        serviceFormattedUsername: importedProfile.formatted_username,
        service: {
          name: importedProfile.service,
          username: importedProfile.service_username,
          formattedUsername: importedProfile.formatted_username,
        },
        images: { avatar: importedProfile.avatar_https },
        timezone: importedProfile.timezone,
        shouldBeAutoSelected: !!preselectedProfileIds.includes(
          importedProfile.id,
        ),
        isSelected: false,
        isDisabled: !!importedProfile?.disabled,
        subprofiles,
        selectedSubprofileId: null,
        serviceType: importedProfile.service_type,
        serviceId: importedProfile.serviceId,
        subprofilesOrignatedFromAPI: null,
        instagramDirectEnabled:
          importedProfile.should_post_direct &&
          importedProfile.service === 'instagram',
        isTwitterPremium: !!importedProfile.isTwitterPremium,
        canAssociateLocation: !!importedProfile.canAssociateLocation,
        locationData: importedProfile.locationData,
        profileHasPostingSchedule: !!importedProfile.schedules.some(
          (item) => item.times.length > 0,
        ),
        defaultToReminders: !!importedProfile.defaultToReminders,
        displayName: importedProfile.displayName,
        serverUrl: importedProfile.serverUrl,
        shorteningDomain: importedProfile.shorteningDomain,
        utmTrackingEnabled:
          importedProfile.googleAnalyticsEnabled === 'enabled',
        showTrendingTopicSuggestions:
          importedProfile.showTrendingTopicSuggestions ?? true,
        // Profile-specific app state
        appState: {
          isSubprofileCreationPending: false,
        },
      }
    })
  },

  formatMetaData: (update: RpcUpdate | PrefillPostData): AppStoreMetaData => {
    const videos = formatRootVideos(update.media, update.extra_media)
    return {
      updateId: update?.id || null,
      scheduledAt: update?.due_at || update?.scheduled_at || null,
      // The following comment comes from the old composer codebase but still somewhat true
      // in that this value is only used at the point of saving an update to the API.
      //
      // Updates that are queued, without a scheduled_at time set by users, will still
      // have a due_at time set by the scheduling engine. Inside MC, we use scheduledAt
      // for the time at which the update will go out, without worrying about its origin.
      // We still keep track of the origin through didUserSetScheduledAt in order to
      // properly format fields for the API. This value isn't imported into the app,
      // and only serves as a reference to previous update state when saving to the API.
      didUserSetScheduledAt: !!(update.due_at && update.scheduled_at),
      text: update?.text ?? '',
      title: update?.title,
      sourceUrl: update.source_url || null,
      userTags: update?.service_user_tags,
      annotations: update?.annotations || [],
      channelData: update?.channelData ?? null,
      updateType: update?.updateType,
      ideaId: update?.ideaId,
      fromIdea: !!update?.ideaId,
      isReminder:
        update?.isReminder !== undefined
          ? Boolean(update.isReminder)
          : update?.postDetails?.isInstagramReminder !== undefined
            ? Boolean(update.postDetails.isInstagramReminder)
            : undefined,
      serviceUpdateId: update?.serviceUpdateId,
      images: formatRootImages(update.media, update.extra_media),
      mediaOrder: formatMediaOrder(update.media, update.extra_media),
      // Video/document are matched across media + extra_media (not media alone)
      // so a channel-less draft mixing an image with a video/document hydrates
      // every attachment. Single-media posts keep extra_media images-only, so
      // this is a no-op for them.
      //
      // `videos` carries every attached video; `video` is its first entry,
      // for call sites that only ever handle one.
      videos,
      video: videos[0] ?? null,
      document: (() => {
        const documentMedia = [
          update.media,
          ...(update.extra_media ?? []),
        ].find(isRpcUpdateDocument)
        return documentMedia ? Document.fromPostMedia(documentMedia) : null
      })(),
      linkData: isRpcUpdateLinkMedia(update.media)
        ? getFormattedLinkData(update.media)
        : null,
      retweetData: update?.retweet
        ? getFormattedRetweetData(update.retweet)
        : null,
      facebookMentionEntities:
        update?.entities && update.entities.length > 0
          ? getFormattedFacebookMentionEntities(update.entities)
          : undefined,
      thread: formatThreadsData(update.thread),
    }
  },
}

export default DataImportUtils
